import { validateOptionalEditorText } from "./editorValidation.js";

export type RamadanModeEditorInput = {
  modeId: string;
  title: string;
  titleAr: string;
  summary: string;
  summaryAr: string;
  slotPriceUsd: string;
};

export type RamadanModeEditorDraft = {
  modeId: string;
  seasonYear: 2027;
  status: "active";
  title: string;
  titleAr: string;
  summary: string;
  summaryAr: string;
  sponsorshipsEnabled: true;
  slotPriceCents: number;
  currency: "usd";
  dayCount: 30;
  source: "experience_four";
};

export type RamadanModeEditorResult =
  | {
      ok: true;
      draft: RamadanModeEditorDraft;
    }
  | {
      ok: false;
      errors: string[];
    };

export type RamadanModeEditorFixture = {
  modeId: string;
  slotPriceCents: number;
  summary: string;
  summaryAr: string;
  title: string;
  titleAr: string;
};

const titleLimit = 120;
const summaryLimit = 800;
const maxSlotPriceCents = 100000;

export function buildRamadanModeEditorInputFromFixture(
  fixture: RamadanModeEditorFixture,
): RamadanModeEditorInput {
  return {
    modeId: fixture.modeId,
    slotPriceUsd: formatUsdInputValue(fixture.slotPriceCents),
    summary: fixture.summary,
    summaryAr: fixture.summaryAr,
    title: fixture.title,
    titleAr: fixture.titleAr,
  };
}

export function buildDefaultRamadanModeEditorInput(): RamadanModeEditorInput {
  return {
    modeId: "ramadan-2027",
    slotPriceUsd: "500",
    summary:
      "Synthetic Ramadan mode for iftar sponsorship readiness and real-time admin visibility.",
    summaryAr: "وضع رمضان التجريبي لجاهزية رعاية الإفطار والرؤية المباشرة للإدارة.",
    title: "Ramadan Operations 2027",
    titleAr: "عمليات رمضان 2027",
  };
}

export function buildEmptyRamadanModeEditorInput(): RamadanModeEditorInput {
  return {
    modeId: "",
    slotPriceUsd: "",
    summary: "",
    summaryAr: "",
    title: "",
    titleAr: "",
  };
}

export function evaluateRamadanModeEditorInput(
  input: RamadanModeEditorInput,
): RamadanModeEditorResult {
  const modeId = normalizeRamadanModeDocumentId(input.modeId);
  const title = input.title.trim();
  const titleAr = input.titleAr.trim();
  const summary = input.summary.trim();
  const summaryAr = input.summaryAr.trim();
  const slotPriceCents = parseUsdInputToCents(input.slotPriceUsd);
  const errors: string[] = [];

  validateRequiredText("Ramadan mode id", modeId, 80, errors);
  validateRequiredText("English title", title, titleLimit, errors);
  validateOptionalEditorText("Arabic title", titleAr, titleLimit, errors);
  validateRequiredText("English summary", summary, summaryLimit, errors);
  validateOptionalEditorText("Arabic summary", summaryAr, summaryLimit, errors);

  if (slotPriceCents === undefined) {
    errors.push("Sponsorship price must be a valid USD amount.");
  } else if (slotPriceCents <= 0 || slotPriceCents > maxSlotPriceCents) {
    errors.push("Sponsorship price must be greater than $0 and at most $1,000.");
  }

  if (errors.length > 0 || slotPriceCents === undefined) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    draft: {
      currency: "usd",
      dayCount: 30,
      modeId,
      seasonYear: 2027,
      slotPriceCents,
      source: "experience_four",
      sponsorshipsEnabled: true,
      status: "active",
      summary,
      summaryAr,
      title,
      titleAr,
    },
  };
}

export function normalizeRamadanModeDocumentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function formatUsdInputValue(amountCents: number): string {
  if (amountCents % 100 === 0) {
    return String(amountCents / 100);
  }

  return (amountCents / 100).toFixed(2);
}

function parseUsdInputToCents(value: string): number | undefined {
  const trimmed = value.trim();

  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return undefined;
  }

  const parts = trimmed.split(".");
  const dollarsPart = parts[0];
  const centsPart = parts[1] ?? "";

  if (!dollarsPart) {
    return undefined;
  }

  const dollars = Number(dollarsPart);

  if (!Number.isSafeInteger(dollars)) {
    return undefined;
  }

  const cents = Number(centsPart.padEnd(2, "0"));
  const totalCents = dollars * 100 + cents;
  return Number.isSafeInteger(totalCents) ? totalCents : undefined;
}

function validateRequiredText(
  label: string,
  value: string,
  maxLength: number,
  errors: string[],
): void {
  if (!value) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}
